import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useFetcher } from "~/hooks/useFetcher";
import {
  SEARCH_ENTITY_TYPES,
  SearchEntityType,
  SearchIndexPendingResponse,
  SearchIndexPendingTypeStatus,
  SearchIndexRun,
  SearchIndexStatus,
  SearchIndexTypeResult,
  SearchOrphanResponse,
  isRunFinished,
  searchIndexOrphansPath,
  searchIndexPath,
  searchIndexPendingPath,
  searchIndexRunPath,
  startSearchIndexRun,
} from "~/services/search";
import { EntityTypeFilter } from "./EntityTypeFilter";
import { Column, EntityTypeTable } from "./EntityTypeTable";
import { IndexContents } from "./IndexContents";
import { IndexHealth } from "./IndexHealth";
import { SampleLine } from "./SampleLine";
import { Section } from "./Section";
import { ENTITY_TYPE_LABEL, errorMessage } from "./constants";

const BATCH_SIZES = [10, 50, 100, 500];

const RUN_POLL_MS = 2000;

const PENDING_COLUMNS: Column<SearchIndexPendingTypeStatus>[] = [
  {
    label: "Collected",
    hint: "How many entities this survey read from the source, newest first. Fewer than you picked means the type has no more.",
    cell: (type) => type.collected,
  },
  {
    label: "Pending",
    hint: "Entities a run would embed. New, Edited and Model out-of-date are what it is made of; the rest of the collected entities are already current.",
    isEmphasized: true,
    cell: (type) => type.pending,
  },
  { label: "New", cell: (type) => type.new },
  { label: "Edited", cell: (type) => type.text_changed },
  {
    label: "Model out-of-date",
    hint: "Indexed under a superseded embedding model. The text is unchanged, but search skips these rows until a run re-embeds them.",
    cell: (type) => type.model_changed,
  },
  {
    label: "No content",
    hint: "Entities with no content to embed. A run neither indexes nor counts these as pending.",
    cell: (type) => type.blank,
  },
];

const RUN_COLUMNS: Column<SearchIndexTypeResult>[] = [
  { label: "Collected", cell: (result) => result.collected },
  {
    label: "Embedded",
    hint: "Entities sent to the embedding model. This is what the run cost; the Unchanged ones were free.",
    cell: (result) => result.embedded,
  },
  {
    label: "Rows written",
    hint: "One row per chunk of an embedded entity, so a long entity writes several.",
    cell: (result) => result.chunks,
  },
  { label: "Unchanged", cell: (result) => result.unchanged },
  { label: "No content", cell: (result) => result.blank },
];

/** What the index holds, and the control that puts things in it. */
export const IndexTab = () => {
  const status = useFetcher<SearchIndexStatus>(searchIndexPath());
  const [entityTypes, setEntityTypes] = useState<SearchEntityType[]>(SEARCH_ENTITY_TYPES);
  const [windowLimit, setWindowLimit] = useState(10);
  // Same types and window as the run, so it previews this button.
  const pending = useFetcher<SearchIndexPendingResponse>(
    searchIndexPendingPath(entityTypes, windowLimit)
  );
  const orphans = useFetcher<SearchOrphanResponse>(searchIndexOrphansPath(entityTypes));
  const [isStarting, setIsStarting] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  const refresh = () => Promise.all([status.mutate(), pending.mutate(), orphans.mutate()]);

  const run = useFetcher<SearchIndexRun>(runId && searchIndexRunPath(runId), {
    refreshInterval: (data) => (data && isRunFinished(data) ? 0 : RUN_POLL_MS),
    onSuccess: (data) => {
      if (isRunFinished(data)) void refresh();
    },
  });
  const isRunning =
    isStarting || (runId !== null && !run.error && !(run.data && isRunFinished(run.data)));
  const runError = startError ?? run.data?.error ?? (run.error && errorMessage(run.error)) ?? null;

  const onRun = async () => {
    setIsStarting(true);
    setStartError(null);
    try {
      setRunId((await startSearchIndexRun(entityTypes, windowLimit)).id);
    } catch (caught) {
      setStartError(errorMessage(caught));
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <IndexHealth status={status} pending={pending} orphans={orphans} />

      <section className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <EntityTypeFilter value={entityTypes} onChange={setEntityTypes} />
          <Select
            value={String(windowLimit)}
            onValueChange={(next) => setWindowLimit(Number(next))}
          >
            <SelectTrigger className="w-40" aria-label="Entities per type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BATCH_SIZES.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  Newest {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={onRun} disabled={isRunning}>
            {isRunning ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <RefreshCw className="size-4" />
            )}
            Index now
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Entities whose text and model version both already match are skipped, so a repeat run
          costs nothing. Datasets are read with your own catalog access.
        </p>
      </section>

      {runError && <p className="text-sm text-destructive">Indexing failed: {runError}</p>}

      <IndexContents status={status} orphans={orphans} onPruned={refresh} />

      <Section title="Needs indexing">
        {pending.error && (
          <p className="text-sm text-destructive">
            Could not survey the sources: {errorMessage(pending.error)}
          </p>
        )}
        {pending.isLoading && <p className="text-sm text-muted-foreground">Surveying…</p>}
        {pending.data && (
          <>
            <EntityTypeTable
              label="Needs indexing"
              rows={pending.data.types}
              columns={PENDING_COLUMNS}
            />
            <div className="flex flex-col gap-1">
              {pending.data.types.every((type) => type.pending === 0) ? (
                <p className="text-xs text-muted-foreground">
                  Everything in the newest {pending.data.window_limit} is indexed. Coverage above
                  says whether older entities are missing.
                </p>
              ) : (
                pending.data.types.map((type) => (
                  <SampleLine
                    key={type.entity_type}
                    label={ENTITY_TYPE_LABEL[type.entity_type]}
                    names={type.sample.map((entity) => entity.title || entity.entity_id)}
                    total={type.pending}
                  />
                ))
              )}
            </div>
          </>
        )}
      </Section>

      {run.data && (
        <Section
          title={
            run.data.completed_at
              ? `Last run · ${Date.parse(run.data.completed_at) - Date.parse(run.data.started_at)}ms`
              : "Running…"
          }
          question="What that run actually did, per type."
        >
          <EntityTypeTable label="Last run" rows={run.data.results} columns={RUN_COLUMNS} />
        </Section>
      )}
    </div>
  );
};
